import {
  Tooltip as TooltipBase,
  TooltipProps as MuiTooltipProps,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface Truncation {
  onTruncation?: (isTruncated: boolean) => void;
}

const getTextWidth = (text: string, font: string): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;

  return context.measureText(text).width;
};

interface TooltipProps extends Truncation, Omit<MuiTooltipProps, 'children'> {
  activateForDisabled?:
    | boolean
    | {
        activeateOnClick?: boolean;
      };
  hideOnClickIfUnhoverd?: boolean;
  hide?: boolean;
  showOnlyOnOverflow?: boolean;
  children: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
}

export const Tooltip: React.FC<TooltipProps> = (props) => {
  const {
    title = '',
    children,
    activateForDisabled: paramActiveForDisabled,
    enterDelay = 0,
    placement,
    hideOnClickIfUnhoverd,
    hide = false,
    disableHoverListener,
    classes,
    disableInteractive = false,
    showOnlyOnOverflow = false,
    onTruncation,
  } = props;

  const activateForDisabled = !!paramActiveForDisabled;
  const activeateOnClick =
    typeof paramActiveForDisabled === 'object' &&
    paramActiveForDisabled.activeateOnClick;

  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const childRef = useRef<HTMLDivElement>(null);

  const isElementTruncated = useCallback(
    (element: HTMLElement): boolean => {
      // When the `element` is an `input` we can't really know
      // if the text truncated or not so we had to check the
      // `title` width if the `title` is a string
      const font = window.getComputedStyle(element).font;
      const textWidth =
        typeof title === 'string' ? getTextWidth(title, font) : 0;

      const hasEllipsis =
        textWidth > element.clientWidth ||
        element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight;

      if (hasEllipsis) return true;

      return false;
    },
    [title]
  );

  useEffect(() => {
    if (showOnlyOnOverflow && childRef.current) {
      setIsTruncated(isElementTruncated(childRef.current));
      if (onTruncation) {
        onTruncation(isElementTruncated(childRef.current));
      }
    }
  }, [children, showOnlyOnOverflow, isElementTruncated, onTruncation]);

  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        event instanceof MouseEvent &&
        childRef.current &&
        childRef.current.contains(event.target as Node)
      ) {
        return;
      }

      setShowTooltip(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleClickOutside);
    };
  }, [showTooltip]);

  const hideOnClickIfUnhoverdHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setTimeout(() => {
        if (!(e.target as Element).matches(':hover')) {
          setShowTooltip(false);
        }
      }, 100);
    },
    []
  );

  const child = useMemo(() => {
    if (React.isValidElement(children)) {
      const childProps = {
        ref: childRef,
        onClick: (e: React.MouseEvent) => {
          if (activateForDisabled && activeateOnClick) {
            if (!disableHoverListener) setShowTooltip((active) => !active);
          }

          if (children.props.onClick) {
            children.props.onClick(e);
          }
        },
      };
      return React.cloneElement(children, childProps);
    }
    return children;
  }, [children, activateForDisabled, activeateOnClick, disableHoverListener]);

  const shouldShowTooltip = useMemo(
    () => !showOnlyOnOverflow || (showOnlyOnOverflow && isTruncated),
    [showOnlyOnOverflow, isTruncated]
  );
  const tooltipTitle = shouldShowTooltip ? title : '';

  const [hoverTimeout, setHoverTimeout] = useState<number | null>(null);
  const handleMouseEnter = () => {
    const timeout = window.setTimeout(
      () => setShowTooltip(true),
      enterDelay ?? 0
    );
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setShowTooltip(false);
  };

  const mouseHoverHandler = !disableHoverListener
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }
    : undefined;

  if (hide && !shouldShowTooltip) return child;

  if (activeateOnClick || hideOnClickIfUnhoverd) {
    const hideOnClickIfUnhoverdConfig = hideOnClickIfUnhoverd
      ? {
          onClick: hideOnClickIfUnhoverdHandler,
          ...mouseHoverHandler,
        }
      : undefined;

    //managed
    return (
      <TooltipBase
        key={'managed'}
        title={tooltipTitle}
        enterDelay={enterDelay}
        arrow
        open={showTooltip}
        onClose={() => setShowTooltip(false)}
        placement={placement}
        {...hideOnClickIfUnhoverdConfig}
        disableHoverListener={disableHoverListener}
        classes={classes}
      >
        {child}
      </TooltipBase>
    );
  }
  //auto
  return (
    <TooltipBase
      key={'auto'}
      title={tooltipTitle}
      enterDelay={enterDelay}
      placement={placement}
      disableInteractive={disableInteractive}
      disableHoverListener={disableHoverListener}
      classes={classes}
    >
      {child}
    </TooltipBase>
  );
};
